// PATH: src/pages/Enlaces.jsx
// VERSION: 2.3 — Categoría debajo de la descripción y bien separada de "Abrir enlace"

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  Input,
  VStack,
  Tag,
  TagLabel,
  HStack,
  Link as CLink,
  Badge,
  Spinner,
  useBreakpointValue,
  useColorModeValue,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
} from '@chakra-ui/react';
import { ExternalLinkIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { API } from '../utils/apiBase';

/* ---------- helpers ---------- */
async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Devuelve una lista plana de links con su categoría
function normalizeLinks(payload) {
  if (!payload) return [];

  if (Array.isArray(payload.categories)) {
    return payload.categories.flatMap(cat =>
      (cat.items || []).map(it => ({
        title: it.title ?? '',
        description: it.description ?? '',
        url: it.url ?? '',
        category:
          cat.name ??
          it.category ??
          it.section ??
          'Otros',
      }))
    );
  }

  if (
    Array.isArray(payload.items) &&
    payload.items.length &&
    payload.items[0]?.items
  ) {
    return payload.items.flatMap(cat =>
      (cat.items || []).map(it => ({
        title: it.title ?? '',
        description: it.description ?? '',
        url: it.url ?? '',
        category:
          cat.name ??
          it.category ??
          it.section ??
          'Otros',
      }))
    );
  }

  const flat =
    payload.links || payload.items || payload.data || payload;
  if (Array.isArray(flat)) {
    return flat.map(it => ({
      title: it.title ?? '',
      description: it.description ?? '',
      url: it.url ?? '',
      category: it.category ?? it.section ?? 'Otros',
    }));
  }

  return [];
}

/* ---------- page ---------- */
export default function Enlaces() {
  const [query, setQuery] = useState('');
  const [selectedCats, setSelectedCats] = useState([]); // multiselect

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const isMdOrBelow = useBreakpointValue({
    base: true,
    md: true,
    lg: false,
  });

  const searchPlaceholder = useBreakpointValue({
    base: 'Buscar…',
    md: 'Buscar por título, descripción o URL…',
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const payload = await fetchJson(
          `${API}/links?limit=1000`
        );
        if (!alive) return;
        setItems(normalizeLinks(payload));
      } catch (e) {
        if (alive)
          setErr(
            e?.message || 'Error al cargar enlaces'
          );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const categories = useMemo(() => {
    const s = new Set(['Todos']);
    (items || []).forEach(i =>
      s.add(i.category || 'Otros')
    );
    return Array.from(s).sort((a, b) =>
      a === 'Todos'
        ? -1
        : b === 'Todos'
          ? 1
          : a.localeCompare(b)
    );
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = items || [];

    if (selectedCats.length) {
      list = list.filter(i =>
        selectedCats.includes(i.category || 'Otros')
      );
    }

    if (!q) return list;

    return list.filter(
      i =>
        (i.title || '')
          .toLowerCase()
          .includes(q) ||
        (i.description || '')
          .toLowerCase()
          .includes(q) ||
        (i.url || '')
          .toLowerCase()
          .includes(q) ||
        (i.category || '')
          .toLowerCase()
          .includes(q)
    );
  }, [items, selectedCats, query]);

  const selectedCatsLabel = useMemo(() => {
    if (!selectedCats.length) return 'Todas las categorías';
    if (selectedCats.length === 1) return selectedCats[0];
    return `${selectedCats.length} categorías seleccionadas`;
  }, [selectedCats]);

  const activeTagBg = useColorModeValue('black', 'white');
  const activeTagColor = useColorModeValue('white', 'black');

  if (loading) {
    return (
      <Box bg="bg.default" flex="1">
        <Container
          maxW="container.xl"
          py={{ base: 8, md: 12 }}
        >
          <HStack justify="center" py={10}>
            <Spinner size="lg" />
            <Text> Cargando enlaces…</Text>
          </HStack>
        </Container>
      </Box>
    );
  }

  if (err) {
    return (
      <Box bg="bg.default" flex="1">
        <Container
          maxW="container.xl"
          py={{ base: 8, md: 12 }}
        >
          <Heading
            size="md"
            color="red.600"
          >
            Error al cargar enlaces
          </Heading>
          <Text color="gray.700" mt={2}>
            {String(err)}
          </Text>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="bg.default" flex="1">
      <Container
        maxW="container.xl"
        py={{ base: 8, md: 12 }}
      >
        <Heading
          textAlign="center"
          mb={6}
          color="text.default"
        >
          Enlaces importantes
        </Heading>

        <VStack align="stretch" spacing={4} mb={6}>
          <Input
            borderColor="border.default"
            borderWidth="2px"
            placeholder={searchPlaceholder}
            value={query}
            onChange={e => setQuery(e.target.value)}
            bg="bg.card"
            _placeholder={{ color: 'text.muted' }}
            _hover={{ borderColor: 'blue' }}
          />

          {isMdOrBelow ? (
            <Menu closeOnSelect={false}>
              <MenuButton
                as={Button}
                variant="outline"
                rightIcon={<ChevronDownIcon />}
                alignSelf="flex-start"
                borderColor="border.default"
                bg="bg.card"
                _hover={{
                  bg: 'black',
                  color: 'white',
                  borderColor: 'gray.700',
                }}
              >
                {selectedCatsLabel}
              </MenuButton>
              <MenuList maxH="260px" overflowY="auto">
                <MenuItem>
                  <Checkbox
                    isChecked={!selectedCats.length}
                    onChange={() => {
                      setSelectedCats([]);
                    }}
                  >
                    Todas
                  </Checkbox>
                </MenuItem>
                {categories
                  .filter(cat => cat !== 'Todos')
                  .map(cat => (
                    <MenuItem key={cat}>
                      <Checkbox
                        isChecked={selectedCats.includes(cat)}
                        onChange={() => {
                          setSelectedCats(prev =>
                            prev.includes(cat)
                              ? prev.filter(
                                c => c !== cat
                              )
                              : [...prev, cat]
                          );
                        }}
                      >
                        {cat}
                      </Checkbox>
                    </MenuItem>
                  ))}
              </MenuList>
            </Menu>
          ) : (
            <HStack spacing={2} overflowX="auto" pb={1}>
              {categories.map(cat => {
                const isActive =
                  cat === 'Todos'
                    ? selectedCats.length === 0
                    : selectedCats.includes(cat);

                return (
                  <Tag
                    key={cat}
                    variant="outline"
                    bg={isActive ? activeTagBg : 'bg.card'}
                    color={isActive ? activeTagColor : 'text.default'}
                    borderColor={
                      isActive ? 'gray.700' : 'border.default'
                    }
                    borderWidth="2px"
                    cursor="pointer"
                    boxShadow={
                      isActive
                        ? '0 0 0 1px rgba(15,23,42,0.5)'
                        : 'none'
                    }
                    _hover={
                      isActive
                        ? {
                          borderColor: 'gray.500',
                          boxShadow:
                            '0 0 0 1px rgba(15,23,42,0.7)',
                        }
                        : {
                          bg: activeTagBg,
                          color: activeTagColor,
                          borderColor: 'gray.700',
                          boxShadow:
                            '0 0 0 1px rgba(15,23,42,0.4)',
                        }
                    }
                    onClick={() => {
                      if (cat === 'Todos') {
                        setSelectedCats([]);
                      } else {
                        setSelectedCats(prev =>
                          prev.includes(cat)
                            ? prev.filter(
                              c => c !== cat
                            )
                            : [...prev, cat]
                        );
                      }
                    }}
                  >
                    <TagLabel>{cat}</TagLabel>
                  </Tag>
                );
              })}
            </HStack>
          )}
        </VStack>

        {filtered.length === 0 ? (
          <Box
            bg="bg.card"
            border="1px solid"
            borderColor="border.default"
            borderRadius="md"
            p={6}
            textAlign="center"
          >
            <Text color="text.muted">
              No se encontraron enlaces con esos filtros.
            </Text>
          </Box>
        ) : (
          <SimpleGrid
            columns={{
              base: 1,
              sm: 2,
              md: 3,
              lg: 4,
            }}
            spacing={6}
          >
            {filtered.map((it, idx) => (
              <Box
                key={`${it.url}-${idx}`}
                bg="bg.card"
                border="1px solid"
                borderColor="border.default"
                borderRadius="xl"
                overflow="hidden"
                boxShadow="sm"
                transition="all .25s ease"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: 'md',
                }}
                p={5}
              >
                {/* Título */}
                <Text
                  color="text.default"
                  fontWeight="semibold"
                  noOfLines={2}
                  mb={2}
                >
                  {it.title}
                </Text>

                {/* Descripción (si existe) */}
                {it.description && (
                  <Text
                    color="text.muted"
                    fontSize="sm"
                    noOfLines={3}
                    mb={3}
                  >
                    {it.description}
                  </Text>
                )}

                {/* Categoría en su propia línea, separada (ellipsis + no overflow) */}
                <Box mb={3} w="100%" minW={0} overflow="hidden">
                  <Badge
                    variant="solid"
                    colorScheme="blue"
                    display="inline-block"
                    maxW="100%"
                    overflow="hidden"
                    verticalAlign="top"
                    title={it.category || 'Otros'}
                  >
                    <Text color="white" as="span" isTruncated maxW="100%" display="block">
                      {it.category || 'Otros'}
                    </Text>
                  </Badge>
                </Box>

                {/* Enlace en otra línea */}
                <CLink
                  href={it.url}
                  isExternal
                  color="accent.600"
                  fontWeight="medium"
                  display="inline-flex"
                >
                  Abrir enlace <ExternalLinkIcon mx="2px" />
                </CLink>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Container>
    </Box>
  );
}
